import { Injectable } from '@angular/core';

import { REFCLI_TYPE } from '@app/baseBtl/context/contract.interface';
import { BasicObject } from '@app/common/models/services.interface';
import { Oauth2RessourceService } from '@app/oauth2/oauth2-resources.service';

import { Observable, forkJoin, from, of, throwError } from 'rxjs';
import { catchError, map, mergeMap, share, tap } from 'rxjs/operators';

import { StorageService } from '../../../base/services/storage.service';
import { RenewStorageService } from '../../../catalog/category/renew/renew-storage.service';
import { DateUniversalBase } from '../../base/class/date-universal.class';
import { ProductsServiceBase } from '../../catalog/products.service';
import { JsonProduct } from '../../catalog/products/interface/context';
import { ContextInterfaceBase } from '../context.interface';
import { ContextServiceBase } from '../context.service';
import { PromoContextServiceBase } from './promo-context.service';

@Injectable({
    providedIn: 'root',
})
export class RenewContextServiceBase implements ContextInterfaceBase {
    public keySerialize: string = 'RenewContextServiceBase';

    public TYPES = {
        MONTH12: {
            LABEL: 'month12',
            DIFF: 0,
        },
        MONTH18: {
            LABEL: 'month18',
            DIFF: 6,
        },
        MONTH24: {
            LABEL: 'month24',
            DIFF: 12,
        },
    };

    private CDC_TYPES: string[] = ['CDC', 'SUPPORT'];

    public status: boolean;
    public idCt: number;
    public contractId: string;
    public idPersonne: number;
    public planType: string;
    public migrateTo: string;
    public type: string;
    public statut: string;
    public skuOffre: string;
    public codeTac: string;
    public phoneId: string;
    public edp: boolean;
    public collaborator: boolean;
    public multiLigne: boolean;
    public amountPaid: number;
    public insurance: boolean;
    public clientType: string;
    public userType: string;
    public isSowo: boolean;
    public edpPhone: boolean;
    public deviceSeniority: number;
    public plan: JsonProduct;
    public isCDC: boolean = false;
    public paymentMethod: string;
    public iban: string;

    constructor(
        protected oauth2Ressource: Oauth2RessourceService,
        protected products: ProductsServiceBase,
        protected promoContextService: PromoContextServiceBase,
        protected renewStorageService: RenewStorageService,
    ) {}

    public getClientType(): string {
        return this.collaborator ? 'collaborator' : 'client';
    }

    public create(contractId: string): Observable<RenewContextServiceBase> {
        const instance: RenewContextServiceBase = new RenewContextServiceBase(
            this.oauth2Ressource,
            this.products,
            this.promoContextService,
            this.renewStorageService,
        );
        this.contractId = contractId;
        return instance.config(contractId);
    }

    public updateContext(contextService: ContextServiceBase): Observable<ContextServiceBase> {
        return this.promoContextService.create().pipe(
            mergeMap((res) => contextService.setContext(res)),
            map(() => contextService),
        );
    }

    public config(contractId: string) {
        this.contractId = contractId;
        return forkJoin([
            this.oauth2Ressource.contracts(contractId).get(),
            this.oauth2Ressource.contracts(contractId).eligibilitesRenouvellement().get(),
            this.oauth2Ressource.contracts(contractId).abonnement().get(),
            this.oauth2Ressource.contracts(contractId).options().get(),
        ]).pipe(
            mergeMap((responses: any) =>
                this.getPlanInfos(responses[2].idOffre).pipe(
                    map((response) => {
                        responses.push(response);
                        return responses;
                    }),
                    catchError(() => throwError(['Offre actuel inconue ou problème de chargement de catalog'])),
                ),
            ),
            mergeMap((responses: any) => {
                this.plan = responses[4];
                // eslint-disable-next-line
                this.isSowo = this.plan?.type_id?.indexOf('plan_sowo') !== -1;

                this.setData(responses);

                if (!this.status) {
                    return this.handleStatusError();
                }

                this.computeLabelType(responses[1].items[0]);

                const observables = [];

                this.idPersonne = responses[0]._links.titulaire.href.split('/').pop();
                observables.push(this.handleClientFacturation());
                observables.push(this.handleClientStatus());

                return forkJoin(observables).pipe(
                    map(() => this),
                    catchError(() => from([this])),
                );
            }),
            share(),
        );
    }

    private handleClientFacturation(): Observable<any> {
        return this.oauth2Ressource
            .personnes(this.idPersonne)
            .comptesFacturation()
            .get()
            .pipe(
                catchError(() => of(null)),
                tap((response: any) => {
                    this.paymentMethod = response ? response.items[0].modePaiement : '';
                    if (response.items[0].modePaiement === 'PRELEVEMENT') {
                        this.iban = response.items[0].compteBancaire ? response.items[0].compteBancaire.iban : '';
                    }
                }),
            );
    }

    private handleClientStatus(): Observable<any> {
        return this.oauth2Ressource
            .personnes(this.idPersonne)
            .get()
            .pipe(
                catchError(() => of(null)),
                tap((response: any) => {
                    this.statut = response.representantLegal ? REFCLI_TYPE.ENTREPRISE : REFCLI_TYPE.INDIVIDU;
                }),
            );
    }

    private computeLabelType(eligibilitesRenouvellement): void {
        if (new Date() > new DateUniversalBase(eligibilitesRenouvellement.dateTarifPreferentiel).value || this.isSowo) {
            this.type = this.TYPES.MONTH24.LABEL;
        } else if (
            new Date() > new DateUniversalBase(eligibilitesRenouvellement.dateTarifPreferentielIntermediaire).value
        ) {
            this.type = this.TYPES.MONTH18.LABEL;
        } else {
            this.type = this.TYPES.MONTH12.LABEL;
        }
    }

    private handleStatusError(): Observable<never> {
        const errors: BasicObject = {};

        errors.message = 'Client non éligible renouvellement';
        errors.status = '403';

        return throwError(errors);
    }

    /**
     * [compatibleSim description]
     * @param               gencode [description]
     * @return         [description]
     */
    public compatibleSim(gencode: string): Observable<any> {
        console.log({
            genCode: gencode,
            codeTac: this.codeTac,
        });
        return this.oauth2Ressource
            .referentiel()
            .produits()
            .compatibiliteSim()
            .setParams({
                genCode: gencode,
                codeTac: this.codeTac,
            })
            .get();
    }

    public serialize() {
        return {
            status: this.status,
            type: this.type,
            client: this.getClientType(),
        };
    }

    protected setData(responses: any) {
        /**
         * responses[0] -- Contrat;
         * responses[1] -- Eligibilité Renouvellement
         * responses[2] -- Abonnement
         * responses[3] -- Options
         * responses[4] -- Offre
         */

        this.idCt = responses[0].idCt;
        this.planType = responses[4] ? responses[4].type_id.replace('_renew', '') : null;
        this.migrateTo = this.planType.replace('plan_sowo', 'plan_premium');
        this.type = null;
        this.skuOffre = responses[4].sku;
        this.codeTac = null;
        this.edp = false;
        this.collaborator = responses[0].contratCollaborateur === true;
        this.multiLigne = true;
        this.amountPaid = responses[2].montantPaye;
        this.insurance = true;
        this.clientType = this.getClientType();
        this.userType = this.getUserType();
        this.isCDC = this.CDC_TYPES.includes(this.userType);
        this.deviceSeniority = responses[1].items[0].ancienneteTerminal;
        this.status = this.isCDC
            ? responses[1].items[0].statut
            : responses[1].items[0].statut && responses[1].items[0].renouvellementAutorise;

        for (const key in responses[2].avantages) {
            if (responses[2].avantages[key].libelle === 'Remise multi-lignes') {
                this.multiLigne = true;
            }
        }
    }

    protected getPlanInfos(sku: string): Observable<object> {
        return this.products.getProductsByGencode(sku);
    }

    public getUserType(): string {
        const user = this.oauth2Ressource.getUserInfo();
        return user.user_type || null;
    }

    public getRenewStorageService(): StorageService {
        return this.renewStorageService;
    }

    public getAddresses(): Observable<any> {
        if (!this.idPersonne) {
            return of([]);
        }
        return this.oauth2Ressource
            .personnes(this.idPersonne)
            .coordonnees()
            .get()
            .pipe(map((res) => res.adressesPostales || []));
    }
}
